import React, { useState, useEffect } from 'react';
import { ClipboardList, ExternalLink, RefreshCw, Palette, Layout } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import api from '../utils/api';

const FormBlockEditor = ({ content, onChange }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    fetchForms();
  }, []);

  useEffect(() => {
    if (content.formId && forms.length > 0) {
      const form = forms.find(f => f.form_id === content.formId);
      setSelectedForm(form);
    }
  }, [content.formId, forms]);

  const fetchForms = async () => {
    try {
      const response = await api.get('/forms');
      setForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSelect = (formId) => {
    const form = forms.find(f => f.form_id === formId);
    onChange({
      ...content,
      formId,
      formName: form?.name || ''
    });
    setSelectedForm(form);
  };

  const updateStyle = (key, value) => {
    onChange({
      ...content,
      style: {
        ...content.style,
        [key]: value
      }
    });
  };

  const activeForms = forms.filter(f => f.status === 'active');
  const style = content.style || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={20} className="text-sky-500" />
          <span className="font-medium text-slate-900">Embed Form</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchForms}
          title="Refresh forms list"
        >
          <RefreshCw size={14} />
        </Button>
      </div>

      <div>
        <Label className="mb-2 block">Select Form</Label>
        {loading ? (
          <div className="text-sm text-slate-500">Loading forms...</div>
        ) : activeForms.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <p className="text-sm text-slate-500 mb-2">No active forms found</p>
            <a 
              href="/forms" 
              target="_blank"
              className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
            >
              Create a form <ExternalLink size={12} />
            </a>
          </div>
        ) : (
          <Select value={content.formId || ''} onValueChange={handleFormSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a form to embed..." />
            </SelectTrigger>
            <SelectContent>
              {activeForms.map(form => (
                <SelectItem key={form.form_id} value={form.form_id}>
                  <div className="flex items-center gap-2">
                    <span>{form.name}</span>
                    <span className="text-xs text-slate-400">
                      ({form.fields?.length || 0} fields)
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Form Preview */}
      {selectedForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-slate-900">{selectedForm.name}</h4>
              {selectedForm.description && (
                <p className="text-sm text-slate-500">{selectedForm.description}</p>
              )}
            </div>
            <Badge variant="outline">{selectedForm.fields?.length || 0} fields</Badge>
          </div>
          
          <div className="space-y-2">
            {(selectedForm.fields || []).slice(0, 5).map((field, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">•</span>
                <span className="text-slate-700">{field.label}</span>
                {field.required && (
                  <span className="text-xs text-red-500">*</span>
                )}
                <span className="text-xs text-slate-400">({field.type})</span>
              </div>
            ))}
            {(selectedForm.fields?.length || 0) > 5 && (
              <div className="text-xs text-slate-400">
                +{selectedForm.fields.length - 5} more fields
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {selectedForm.submission_count || 0} submissions
            </span>
            <a
              href={`/forms/${selectedForm.form_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
            >
              Edit form <ExternalLink size={10} />
            </a>
          </div>
        </div>
      )}

      {/* Layout & Styling Controls */}
      {content.formId && (
        <details className="group border border-slate-200 rounded-lg">
          <summary className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-t-lg hover:bg-slate-100">
            <Layout size={14} className="text-indigo-500" />
            <span className="text-sm font-medium">Layout & Styling</span>
          </summary>
          <div className="p-4 space-y-4">
            {/* Layout Options */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1.5 block">Layout</Label>
                <Select 
                  value={style.layout || 'stacked'} 
                  onValueChange={(v) => updateStyle('layout', v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stacked">Stacked (1 Column)</SelectItem>
                    <SelectItem value="two-column">2 Columns</SelectItem>
                    <SelectItem value="three-column">3 Columns</SelectItem>
                    <SelectItem value="inline">Inline (Fields + Button)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Form Width</Label>
                <Select 
                  value={style.formWidth || '100'} 
                  onValueChange={(v) => updateStyle('formWidth', v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25%</SelectItem>
                    <SelectItem value="33">33%</SelectItem>
                    <SelectItem value="40">40%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="60">60%</SelectItem>
                    <SelectItem value="70">70%</SelectItem>
                    <SelectItem value="80">80%</SelectItem>
                    <SelectItem value="90">90%</SelectItem>
                    <SelectItem value="100">100%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Field Styling */}
            <div>
              <Label className="text-xs mb-2 block font-medium flex items-center gap-1">
                <span>Field Styling</span>
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Field Width</Label>
                  <Select 
                    value={style.fieldWidth || '100'} 
                    onValueChange={(v) => updateStyle('fieldWidth', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25%</SelectItem>
                      <SelectItem value="33">33%</SelectItem>
                      <SelectItem value="40">40%</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="60">60%</SelectItem>
                      <SelectItem value="70">70%</SelectItem>
                      <SelectItem value="80">80%</SelectItem>
                      <SelectItem value="90">90%</SelectItem>
                      <SelectItem value="100">100%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Border Radius</Label>
                  <Select 
                    value={style.fieldRadius || 'rounded'} 
                    onValueChange={(v) => updateStyle('fieldRadius', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Square</SelectItem>
                      <SelectItem value="rounded">Rounded</SelectItem>
                      <SelectItem value="pill">Pill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Field Size</Label>
                  <Select 
                    value={style.fieldSize || 'md'} 
                    onValueChange={(v) => updateStyle('fieldSize', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">Small</SelectItem>
                      <SelectItem value="md">Medium</SelectItem>
                      <SelectItem value="lg">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Button Styling */}
            <div>
              <Label className="text-xs mb-2 block font-medium flex items-center gap-1">
                <Palette size={12} className="text-indigo-500" />
                <span>Button Styling</span>
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Button Width</Label>
                  <Select 
                    value={style.buttonWidth || 'auto'} 
                    onValueChange={(v) => updateStyle('buttonWidth', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="75">75%</SelectItem>
                      <SelectItem value="100">Full Width</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Button Style</Label>
                  <Select 
                    value={style.buttonStyle || 'solid'} 
                    onValueChange={(v) => updateStyle('buttonStyle', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Solid</SelectItem>
                      <SelectItem value="outline">Outline</SelectItem>
                      <SelectItem value="ghost">Ghost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Button Radius</Label>
                  <Select 
                    value={style.buttonRadius || 'rounded'} 
                    onValueChange={(v) => updateStyle('buttonRadius', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Square</SelectItem>
                      <SelectItem value="rounded">Rounded</SelectItem>
                      <SelectItem value="pill">Pill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Button Colors */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Button Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={style.buttonColor || '#6366f1'}
                      onChange={(e) => updateStyle('buttonColor', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 cursor-pointer"
                    />
                    <Input
                      value={style.buttonColor || '#6366f1'}
                      onChange={(e) => updateStyle('buttonColor', e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Text Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={style.buttonTextColor || '#ffffff'}
                      onChange={(e) => updateStyle('buttonTextColor', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 cursor-pointer"
                    />
                    <Input
                      value={style.buttonTextColor || '#ffffff'}
                      onChange={(e) => updateStyle('buttonTextColor', e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Alignment</Label>
                  <Select 
                    value={style.buttonAlign || 'left'} 
                    onValueChange={(v) => updateStyle('buttonAlign', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Button Text */}
              <div className="mt-3">
                <Label className="text-xs mb-1.5 block text-slate-500">Button Text</Label>
                <Input
                  value={style.buttonText || 'Submit'}
                  onChange={(e) => updateStyle('buttonText', e.target.value)}
                  placeholder="Submit"
                  className="h-8 text-xs"
                />
              </div>
            </div>

            {/* Label Styling */}
            <div>
              <Label className="text-xs mb-2 block font-medium">Label Styling</Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Label Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={style.labelColor || '#374151'}
                      onChange={(e) => updateStyle('labelColor', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 cursor-pointer"
                    />
                    <Input
                      value={style.labelColor || '#374151'}
                      onChange={(e) => updateStyle('labelColor', e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Label Size</Label>
                  <Select 
                    value={style.labelSize || 'sm'} 
                    onValueChange={(v) => updateStyle('labelSize', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xs">Extra Small</SelectItem>
                      <SelectItem value="sm">Small</SelectItem>
                      <SelectItem value="md">Medium</SelectItem>
                      <SelectItem value="lg">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Label Weight</Label>
                  <Select 
                    value={style.labelWeight || 'medium'} 
                    onValueChange={(v) => updateStyle('labelWeight', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="semibold">Semibold</SelectItem>
                      <SelectItem value="bold">Bold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Spacing */}
            <div>
              <Label className="text-xs mb-2 block font-medium">Spacing</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Field Gap</Label>
                  <Select 
                    value={style.fieldGap || '16'} 
                    onValueChange={(v) => updateStyle('fieldGap', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">Compact (8px)</SelectItem>
                      <SelectItem value="12">Small (12px)</SelectItem>
                      <SelectItem value="16">Medium (16px)</SelectItem>
                      <SelectItem value="24">Large (24px)</SelectItem>
                      <SelectItem value="32">Extra Large (32px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Form Padding</Label>
                  <Select 
                    value={style.formPadding || '0'} 
                    onValueChange={(v) => updateStyle('formPadding', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">None</SelectItem>
                      <SelectItem value="16">Small (16px)</SelectItem>
                      <SelectItem value="24">Medium (24px)</SelectItem>
                      <SelectItem value="32">Large (32px)</SelectItem>
                      <SelectItem value="48">Extra Large (48px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Background */}
            <div>
              <Label className="text-xs mb-2 block font-medium">Background</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1.5 block text-slate-500">Background Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={style.backgroundColor || '#ffffff'}
                      onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 cursor-pointer"
                    />
                    <Input
                      value={style.backgroundColor || '#ffffff'}
                      onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                      className="h-8 text-xs font-mono flex-1"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={style.transparentBg || false}
                      onChange={(e) => updateStyle('transparentBg', e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-xs text-slate-600">Transparent</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </details>
      )}

      {/* Live Styled Preview */}
      {selectedForm && content.formId && (
        <details className="group border border-slate-200 rounded-lg" open>
          <summary className="flex items-center gap-2 cursor-pointer p-3 bg-indigo-50 rounded-t-lg hover:bg-indigo-100">
            <ExternalLink size={14} className="text-indigo-500" />
            <span className="text-sm font-medium text-indigo-700">Live Preview</span>
          </summary>
          <div className="p-4 bg-slate-50 rounded-b-lg overflow-auto">
            <FormStylePreview form={selectedForm} style={style} />
          </div>
        </details>
      )}

      <p className="text-xs text-slate-400">
        This form will be displayed on the published page. Visitors can submit responses.
      </p>
    </div>
  );
};

// Live preview component that renders the form with applied styles
const FormStylePreview = ({ form, style }) => {
  const {
    layout = 'stacked',
    formWidth = '100',
    fieldWidth = '100',
    fieldRadius = 'rounded',
    fieldSize = 'md',
    buttonWidth = 'auto',
    buttonStyle: btnStyle = 'solid',
    buttonRadius = 'rounded',
    buttonColor = '#6366f1',
    buttonTextColor = '#ffffff',
    buttonAlign = 'left',
    buttonText,
    labelColor = '#374151',
    labelSize = 'sm',
    labelWeight = 'medium',
    fieldGap = '16',
    formPadding = '0',
    backgroundColor = '#ffffff',
    transparentBg = false,
  } = style || {};

  // Helper functions
  const getFieldRadius = () => {
    switch (fieldRadius) {
      case 'none': return '0';
      case 'pill': return '9999px';
      default: return '8px';
    }
  };

  const getButtonRadius = () => {
    switch (buttonRadius) {
      case 'none': return '0';
      case 'pill': return '9999px';
      default: return '8px';
    }
  };

  const getFieldPadding = () => {
    switch (fieldSize) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 1rem';
    }
  };

  const getLabelSize = () => {
    switch (labelSize) {
      case 'xs': return '0.75rem';
      case 'md': return '1rem';
      case 'lg': return '1.125rem';
      default: return '0.875rem';
    }
  };

  const getLabelWeight = () => {
    switch (labelWeight) {
      case 'normal': return '400';
      case 'semibold': return '600';
      case 'bold': return '700';
      default: return '500';
    }
  };

  const getLayoutClass = () => {
    switch (layout) {
      case 'inline': return 'flex flex-wrap items-end';
      case 'two-column': return 'grid grid-cols-2';
      case 'three-column': return 'grid grid-cols-3';
      default: return 'flex flex-col';
    }
  };

  const getFieldWrapperStyle = () => {
    if (layout === 'two-column' || layout === 'three-column') {
      return { width: '100%' };
    }
    if (layout === 'inline') {
      return { width: `${fieldWidth}%`, paddingRight: '0.5rem', flexShrink: 0 };
    }
    return { width: `${fieldWidth}%`, margin: fieldWidth !== '100' ? '0 auto' : undefined };
  };

  const formContainerStyle = {
    backgroundColor: transparentBg ? 'transparent' : backgroundColor,
    borderRadius: getFieldRadius(),
    padding: formPadding !== '0' ? `${formPadding}px` : '24px',
    width: `${formWidth}%`,
    margin: formWidth !== '100' ? '0 auto' : undefined,
    border: transparentBg ? 'none' : '1px solid #e2e8f0',
  };

  const inputStyle = {
    width: '100%',
    padding: getFieldPadding(),
    borderRadius: getFieldRadius(),
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    fontSize: '0.875rem',
  };

  const labelStyle = {
    color: labelColor,
    fontSize: getLabelSize(),
    fontWeight: getLabelWeight(),
    marginBottom: '0.25rem',
    display: 'block',
  };

  const buttonContainerStyle = {
    textAlign: buttonAlign,
    marginTop: layout === 'inline' ? '0' : '1.5rem',
  };

  const computedButtonStyle = {
    backgroundColor: btnStyle === 'solid' ? buttonColor : 'transparent',
    color: btnStyle === 'solid' ? buttonTextColor : buttonColor,
    borderRadius: getButtonRadius(),
    border: btnStyle === 'outline' ? `2px solid ${buttonColor}` : 'none',
    width: buttonWidth === 'auto' ? 'auto' : `${buttonWidth}%`,
    padding: '0.75rem 1.5rem',
    fontWeight: '500',
    cursor: 'pointer',
  };

  return (
    <div style={formContainerStyle}>
      <div className={getLayoutClass()} style={{ gap: `${fieldGap}px` }}>
        {(form.fields || []).slice(0, 4).map((field, i) => (
          <div key={i} style={getFieldWrapperStyle()}>
            <label style={labelStyle}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea style={{ ...inputStyle, minHeight: '60px' }} placeholder={field.placeholder} disabled />
            ) : field.type === 'select' ? (
              <select style={inputStyle} disabled>
                <option>{field.placeholder || 'Select...'}</option>
              </select>
            ) : (
              <input type={field.type} style={inputStyle} placeholder={field.placeholder} disabled />
            )}
          </div>
        ))}
        {layout === 'inline' && (
          <div style={{ flexShrink: 0 }}>
            <button type="button" style={computedButtonStyle} disabled>
              {buttonText || form.submit_button_text || 'Submit'}
            </button>
          </div>
        )}
      </div>
      {(form.fields?.length || 0) > 4 && (
        <p className="text-xs text-slate-400 mt-2">+{form.fields.length - 4} more fields...</p>
      )}
      {layout !== 'inline' && (
        <div style={buttonContainerStyle}>
          <button type="button" style={computedButtonStyle} disabled>
            {buttonText || form.submit_button_text || 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FormBlockEditor;
